import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const BranchContext = createContext(null);

export function BranchProvider({ children, user }) {
  const [allBranches, setAllBranches] = useState([]);
  const [accessibleBranches, setAccessibleBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [headquarters, setHeadquarters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAllAccess, setHasAllAccess] = useState(true);

  // Load branches on mount
  const loadBranches = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/branches`);
      const branchList = response.data || [];
      
      // Find headquarters branch
      const hqBranch = branchList.find(b => b.is_headquarters);
      setHeadquarters(hqBranch || null);
      
      // Only show active branches
      const activeBranches = branchList.filter(b => b.is_active !== false);
      setAllBranches(activeBranches);

      // Filter branches based on user's branch_ids
      const userBranchIds = user?.branch_ids || [];
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      
      // If admin or no branch restrictions, user has access to all active branches
      if (isAdmin || userBranchIds.length === 0) {
        setAccessibleBranches(activeBranches);
        setHasAllAccess(true);
      } else {
        // Filter to only accessible branches
        const filtered = activeBranches.filter(b => userBranchIds.includes(b.id));
        setAccessibleBranches(filtered);
        setHasAllAccess(false);
      }

      // Load saved branch from localStorage
      const savedBranchId = localStorage.getItem('currentBranchId');
      
      if (savedBranchId) {
        const savedBranch = activeBranches.find(b => b.id === savedBranchId);
        // Only set if user has access to this branch
        if (savedBranch && (isAdmin || userBranchIds.length === 0 || userBranchIds.includes(savedBranchId))) {
          setCurrentBranch(savedBranch);
        } else if (!isAdmin && userBranchIds.length > 0) {
          // If user has restricted access and saved branch is not accessible, set to first accessible
          const firstAccessible = activeBranches.find(b => userBranchIds.includes(b.id));
          if (firstAccessible) {
            setCurrentBranch(firstAccessible);
            localStorage.setItem('currentBranchId', firstAccessible.id);
          }
        } else if (hqBranch) {
          // Default to HQ for admins
          setCurrentBranch(hqBranch);
          localStorage.setItem('currentBranchId', hqBranch.id);
        }
      } else if (!isAdmin && userBranchIds.length > 0) {
        // Restricted user with no saved branch - default to their first branch
        const firstAccessible = activeBranches.find(b => userBranchIds.includes(b.id));
        if (firstAccessible) {
          setCurrentBranch(firstAccessible);
          localStorage.setItem('currentBranchId', firstAccessible.id);
        }
      } else if (hqBranch) {
        // Default to HQ for admins with no saved preference
        setCurrentBranch(hqBranch);
        localStorage.setItem('currentBranchId', hqBranch.id);
      }
    } catch (error) {
      console.error('Error loading branches:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadBranches();
    }
  }, [user, loadBranches]);

  // Switch to a different branch
  const switchBranch = (branch) => {
    setCurrentBranch(branch);
    if (branch) {
      localStorage.setItem('currentBranchId', branch.id);
    } else {
      localStorage.removeItem('currentBranchId');
    }
  };

  // Get the branch filter for API calls
  const getBranchFilter = () => {
    if (currentBranch) {
      return { branch_id: currentBranch.id };
    }
    return {}; // No filter = all branches
  };

  // Get branch name by ID
  const getBranchName = (branchId) => {
    if (!branchId) return 'Unknown';
    const branch = allBranches.find(b => b.id === branchId);
    return branch ? branch.name : 'Unknown';
  };

  // Check if a branch is headquarters
  const isHeadquarters = (branchId) => {
    if (!branchId || !headquarters) return false;
    return branchId === headquarters.id;
  };

  const value = {
    branches: accessibleBranches, // Only accessible branches for selector
    allBranches, // All branches for admin features
    currentBranch,
    headquarters,
    switchBranch,
    getBranchFilter,
    getBranchName,
    isHeadquarters,
    loading,
    loadBranches,
    hasAllAccess, // Whether user can see "All Branches" option
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
}

export default BranchContext;
